import { ReactNode } from "react";
import { DateTimeModel, ProgressLineProps, ShipmentType, StatusProps } from "./shipmentCard.types";
import { Nullable, ValueOf } from "shared/lib/typeCheck.lib";
import { t } from "shared/localization/localization";

export type RenderSeaCardProps = {
  shipmentType: ValueOf<typeof ShipmentType>;
  onUpdate?: () => void;
  onDelete: () => void;
  onOpen: () => void;
  onFollow?: () => void;
  referenceType: ValueOf<typeof SeaReferenceTypesModel> | null;
  containersAmount: number;
  reference: Nullable<string>;
  status: ValueOf<typeof SeaStatusesModel> | null;
  isUpdating?: boolean;
  className?: string;
  directionFrom: string | null;
  directionTo: string | null;
  progressLine: ProgressLineProps;
  etd: DateTimeModel | null;
  eta: DateTimeModel | null;
  ata: DateTimeModel | null;
  atd: DateTimeModel | null;
  predictiveETA: DateTimeModel | null;
  carrierName: string | null;
  isFollowLoading?: boolean;
  isDeleteProcessing?: boolean;
  canUpdate?: boolean;
  containersSizeTypes: string[];
  scac: string | null;
  tags?: string[];
};

export const SeaReferenceTypesDTO = {
  CT: "CT",
  BL: "BL",
  BK: "BK",
} as const;

export const SeaReferenceTypesModel = {
  ...SeaReferenceTypesDTO,
} as const;

export const SeaRouteTypesDTO = {
  SEA: "SEA",
  LAND: "LAND",
  AIR: "AIR",
} as const;

export const SeaRouteTypesModel = {
  ...SeaRouteTypesDTO,
} as const;

export const SeaTransportTypesDTO = {
  TRUCK: "TRUCK",
  VESSEL: "VESSEL",
  RAIL: "RAIL",
  BARGE: "BARGE",
  FEEDER: "FEEDER",
  AIR: "AIR",
} as const;

export const SeaTransportTypesModel = {
  ...SeaTransportTypesDTO,
} as const;

export const ShipmentStatusesDTO = {
  UNKNOWN: "UNKNOWN",
  PLANNED: "PLANNED",
  IN_TRANSIT: "IN_TRANSIT",
  DELIVERED: "DELIVERED",
  CANCELLED: "CANCELLED",
} as const;

export const SeaStatusesModel = {
  ...ShipmentStatusesDTO,
} as const;

export const seaStatusLabelMap: Record<ValueOf<typeof SeaStatusesModel>, string> = {
  [SeaStatusesModel.UNKNOWN]: t("Unknown"),
  [SeaStatusesModel.PLANNED]: t("Planned"),
  [SeaStatusesModel.IN_TRANSIT]: t("In Transit"),
  [SeaStatusesModel.DELIVERED]: t("Delivered"),
  [SeaStatusesModel.CANCELLED]: t("Cancelled"),
};

/** Qubictron ocean `status_code` values. */
export const SeaMessagesDTO = {
  OK: "OK",
  CARRIER_INFO_NOT_PROVIDED: "CARRIER_INFO_NOT_PROVIDED",
  CARRIER_SHIPMENT_CANCELED: "CARRIER_SHIPMENT_CANCELED",
  NO_CONTAINERS: "NO_CONTAINERS",
  NO_EVENTS: "NO_EVENTS",
  WRONG_PARAMETERS: "WRONG_PARAMETERS",
  WRONG_CARRIER_CODE: "WRONG_CARRIER_CODE",
  WRONG_REFERENCE_NUMBER: "WRONG_REFERENCE_NUMBER",
  WRONG_REFERENCE_TYPE: "WRONG_REFERENCE_TYPE",
  CARRIER_NOT_SUPPORTED: "CARRIER_NOT_SUPPORTED",
  CARRIER_NOT_SUPPORT_REF_TYPE: "CARRIER_NOT_SUPPORT_REF_TYPE",
  CARRIER_TEMPORARILY_DISABLED: "CARRIER_TEMPORARILY_DISABLED",
  CARRIER_UNDER_MAINTENANCE: "CARRIER_UNDER_MAINTENANCE",
  CARRIER_NO_RESPONSE: "CARRIER_NO_RESPONSE",
  AUTO_CANT_DETECT_CARRIER: "AUTO_CANT_DETECT_CARRIER",
  AUTO_CANT_FIND_INFO: "AUTO_CANT_FIND_INFO",
  INVALID_API_KEY: "INVALID_API_KEY",
  API_KEY_ACCESS_DENIED: "API_KEY_ACCESS_DENIED",
  API_KEY_EXPIRED: "API_KEY_EXPIRED",
  API_KEY_QUOTA_EXCEEDED: "API_KEY_QUOTA_EXCEEDED",
  API_KEY_RATE_LIMIT_EXCEEDED: "API_KEY_RATE_LIMIT_EXCEEDED",
  UNEXPECTED_ERROR: "UNEXPECTED_ERROR",
} as const;

export const SeaMessagesModel = {
  ...SeaMessagesDTO,
} as const;

export const seaErrorMessageMap: Record<ValueOf<typeof SeaMessagesModel>, string> = {
  [SeaMessagesModel.OK]: "OK",
  [SeaMessagesModel.CARRIER_INFO_NOT_PROVIDED]: "Shipping line hasn't provided info yet",
  [SeaMessagesModel.CARRIER_SHIPMENT_CANCELED]:
    "The shipping line has informed us that the shipment has been cancelled",
  [SeaMessagesModel.NO_CONTAINERS]: "No containers",
  [SeaMessagesModel.NO_EVENTS]: "No events",
  [SeaMessagesModel.WRONG_PARAMETERS]: "Incorrect request parameters",
  [SeaMessagesModel.WRONG_CARRIER_CODE]: "Incorrect carrier code",
  [SeaMessagesModel.WRONG_REFERENCE_NUMBER]: "Incorrect shipment tracking number",
  [SeaMessagesModel.WRONG_REFERENCE_TYPE]: "Incorrect type of shipment tracking number",
  [SeaMessagesModel.CARRIER_NOT_SUPPORTED]: "This carrier is not supported",
  [SeaMessagesModel.CARRIER_NOT_SUPPORT_REF_TYPE]: "This carrier does not support the selected reference type",
  [SeaMessagesModel.CARRIER_TEMPORARILY_DISABLED]: "This carrier is temporarily unavailable",
  [SeaMessagesModel.CARRIER_UNDER_MAINTENANCE]: "This carrier is under maintenance",
  [SeaMessagesModel.CARRIER_NO_RESPONSE]: "An error occurred while executing the request. Repeat the request later",
  [SeaMessagesModel.AUTO_CANT_DETECT_CARRIER]: "We were unable to automatically determine the shipping line",
  [SeaMessagesModel.AUTO_CANT_FIND_INFO]: "We were unable to find tracking information",
  [SeaMessagesModel.INVALID_API_KEY]: "Invalid API key",
  [SeaMessagesModel.API_KEY_ACCESS_DENIED]: "API key access denied",
  [SeaMessagesModel.API_KEY_EXPIRED]: "API key expired",
  [SeaMessagesModel.API_KEY_QUOTA_EXCEEDED]: "API key limit reached",
  [SeaMessagesModel.API_KEY_RATE_LIMIT_EXCEEDED]: "API key rate limit exceeded",
  [SeaMessagesModel.UNEXPECTED_ERROR]: "Unexpected error. Please try again later",
};

type ErrorStatus = "not-defined" | "error" | "warn";

export type RenderSeaSearchErrorCardProps = {
  className?: string;
  reference?: string | null;
  referenceType?: Nullable<ValueOf<typeof SeaReferenceTypesModel>>;
  text?: ReactNode;
  status?: ErrorStatus;
  containerStatus?: StatusProps["status"];
  canChangeShippingLine?: boolean;
  followLoading?: boolean;
  deleteLoading?: boolean;
  onSelect?: () => void;
  onRetry?: () => void;
  onFollow?: () => void;
  onDelete?: () => void;
};

export const changeShippinglineErrorMessages = [
  SeaMessagesModel.AUTO_CANT_DETECT_CARRIER,
  SeaMessagesModel.CARRIER_INFO_NOT_PROVIDED,
  SeaMessagesModel.WRONG_REFERENCE_NUMBER,
];
